
// @ts-ignore
import {translate} from '@elements/translations';
import {ref, watch, onBeforeMount, reactive, onMounted, defineComponent} from 'vue';
import Breadcrumb from "../../components/breadcrumb/Breadcrumb.vue";
import {useTicketStore} from '../../stores/TicketStore';
import {storeToRefs} from 'pinia'
import LoadingSpinner from "../../components/loading/LoadingSpinner.vue";
import {PricingStruct} from "../../utils/structs";
import {
    addParamsToUrl,
    assertData,
    fetcher,
    areEqualArrays,
    throwAlertMessage,
    convertInCurrency
} from "../../utils/utils";
import {getSkiTicketBaseConfig} from "../../utils/config-helper";
import NumberSpinner from "../../components/form-elements/NumberSpinner.vue";
import PriceGroupConfigurations from "../../components/configurations/PriceGroupConfigurations.vue";
import LoginForm from "../../components/login/LoginForm.vue";
import ProfilePersonConfiguration from "../../components/configurations/ProfilePersonConfiguration.vue";
import {useLoginStore} from "../../stores/LoginStore.ts";
import {postFetcher} from "../../utils/utils";
import TicketConfigurations from "../../components/configurations/TicketConfigurations.vue";
import EditModal from "../../components/modal/EditModal.vue";

export default defineComponent({
    name: 'PersonsView',
    components: {
        EditModal,
        TicketConfigurations,
        ProfilePersonConfiguration,
        NumberSpinner,
        Breadcrumb,
        LoadingSpinner,
        PriceGroupConfigurations,
        LoginForm
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        ticketId: {
            type: String,
            required: true,
        },
        stepViews: {
            type: Array,
            required: true
        },
        isLoggedIn: {
            type: Boolean,
            required: true
        },
        isPriceCalculator: {
            type: Boolean,
            required: false,
            default: false
        },
        selectedDateStart: {
            type: String,
            required: false,
        },
        selectedDateEnd: {
            type: String,
            required: false,
        },
    },
    setup(props) {
        const ticketStore = useTicketStore();
        const { specialPriceModals } = storeToRefs(ticketStore);
        const {
            step,
            selectedDateStart,
            selectedDateEnd,
            selectedOption,
            selectedInsurance,
            selectedUpgrades,
            selectedPriceGroup,
            selectedCurrency,
            selectedPriceConfiguration,
            allPriceGroups,
            ticketConfigurations,
        } = storeToRefs(ticketStore);

        const {
            changeStep,
            changePriceGroup,
            changePriceConfiguration,
            changeTicketConfigurations,
            changeAllPriceGroups,
            changeFinalPrice,
            changeOption,
        } = ticketStore;

        const loginStore = useLoginStore();
        const {profilePersons} = storeToRefs(loginStore);
        const {changeProfilePersons} = loginStore;

        const isLoading = ref<boolean>(false);
        const priceGroups = ref<object>([]);
        const priceConfigurations = ref<object>([]);
        const isMobile = matchMedia('(max-width: 767px)').matches;
        const priceLoaded = ref<boolean>(false);
        const priceGroupCounter = ref<Object>({});
        const { setSpecialPriceModals } = ticketStore;

        const onChangeStep = (step: number) => {
            changeStep(step);
        }

        const onChangePriceGroupCounter = (counter: number, item: Object) => {
            let ticketConfig = ticketConfigurations.value;
            let priceGroup = item.id;
            ticketConfig['priceGroups'][priceGroup].counter = counter;
            ticketConfig['priceGroups'][priceGroup].price = item.price;
            changeTicketConfigurations(ticketConfig);
            calculatePrice();
            if (priceGroup) {
                checkSpecialPriceModal(parseInt(priceGroup), counter > 0);
            }
        }

        const getConfigurationPrice = () => {
            let configuration = [];
            try {
                configuration = priceConfigurations.value.filter(config => config.option === selectedOption.value
                    && config.insurance === selectedInsurance.value
                    && areEqualArrays(config.upgrades, selectedUpgrades.value));
                changePriceConfiguration(configuration[0]);
            } catch (error) {
                console.error(error);
                let message = `<strong>option</strong>: ${selectedOption.value}, <br><strong>insurance</strong>: ${selectedInsurance.value}, <br> <strong>upgrades:</strong> ${selectedUpgrades.value},  <br> <strong>priceGroup:</strong> ${selectedPriceGroup.value}`;
                throwAlertMessage("danger", "No priceConfig found", message + "<br>" + error, true);
                return 0;
            }
        }

        const updateCounters = () => {
            let ticketConfig = ticketConfigurations.value;
            allPriceGroups.value.forEach(function (element, index) {
                priceGroupCounter.value[element.value] = 0;
            });

            allPriceGroups.value.forEach(function (element, index) {
                priceGroupCounter.value[element.value] = 0;
            });

            Object.keys(ticketConfig['priceGroups']).forEach(function(key) {
                let priceGroup = ticketConfig['priceGroups'][key];
                if (priceGroup.counter > 0) {
                    priceGroupCounter.value[key] += priceGroup.counter;
                }
            });

            Object.keys(ticketConfigurations.value.persons).forEach(function(key) {
                let person = ticketConfigurations.value.persons[key];
                if (person.counter > 0) {
                    priceGroupCounter.value[person.priceGroup] += person.counter;
                }
            });

            Object.keys(ticketConfig['priceGroups']).forEach(function(key) {
                let priceGroup = ticketConfig['priceGroups'][key];
                if (priceGroup.id) {
                    checkSpecialPriceModal(parseInt(priceGroup.id), priceGroup.counter > 0);
                }
            });
        }

        const fetchPrices = async () => {
            isLoading.value = true;

            const params = {
                ticketId: props.ticketId,
                ...(selectedDateStart.value !== "" && {selectedDateStart: selectedDateStart.value}),
                ...(selectedDateEnd.value !== "" && {selectedDateEnd: selectedDateEnd.value}),
            };

            let url = getSkiTicketBaseConfig().pricings;
            url = addParamsToUrl(url, new URLSearchParams(params));
            await fetcher(url).then((result) => {
                assertData(result, PricingStruct, url, 'fetchPrices', 'danger');

                result.priceGroups.forEach(function (element, index) {
                    priceGroups.value.push({
                        label: element.label,
                        labelPlural: element.labelPlural,
                        value: element.id
                    })
                });

                if (result.specialPriceModals) {
                    setSpecialPriceModals(result.specialPriceModals);
                } else {
                    setSpecialPriceModals([]);
                }

                priceConfigurations.value = result.priceConfigurations;
                changeAllPriceGroups(priceGroups.value);

                if (!selectedPriceGroup.value) {
                    changePriceGroup(priceGroups.value[0].value);
                }

                if (!selectedOption.value) {
                    changeOption(priceConfigurations.value[0].option);
                }

                let ticketConfig = ticketConfigurations.value;
                allPriceGroups.value.forEach((priceGroup) => {
                    if(ticketConfig['priceGroups'][priceGroup.value] === undefined){
                        ticketConfig['priceGroups'][priceGroup.value] = {};
                    }
                    ticketConfig['priceGroups'][priceGroup.value].counter = 0;
                });
                
                changeTicketConfigurations(ticketConfig);
                getConfigurationPrice();
                calculatePrice();
            }).then(() => {
                isLoading.value = false;
                priceLoaded.value = true;
            }).catch((error) => {
                isLoading.value = false;
                console.log(error);
            });
        };

        onMounted(() => {
            if (Object.keys(selectedPriceConfiguration.value).length === 0) {
                fetchPrices();
            } else if(props.isLoggedIn){
                fetchPersons();
            }

            updateCounters();
        });

        watch(priceLoaded, () => {
            if(props.isLoggedIn && Object.keys(profilePersons.value).length === 0){
                fetchPersons();
            }
        });

        watch(ticketConfigurations.value, () => {
            updateCounters();
        });

        const onLoggedInData = (data) => {
            changeProfilePersons(data.persons);
        }

        const fetchPersons = async () => {
            if(props.isLoggedIn){
                isLoading.value = true;

                const loginForm = reactive({
                    ticketId: props.ticketId,
                    ...(selectedDateStart.value !== "" && {selectedDateStart: selectedDateStart.value}),
                    ...(selectedPriceConfiguration.value.upgradeOption !== undefined && {upgradeOption: selectedPriceConfiguration.value.upgradeOption}),
                    ...(selectedPriceConfiguration.value.upgradeCatalog !== undefined && {upgradeCatalog: selectedPriceConfiguration.value.upgradeCatalog}),
                    ...(selectedPriceConfiguration.value.option !== undefined && {option: selectedPriceConfiguration.value.option})
                });

                let url = getSkiTicketBaseConfig().persons;
                url = addParamsToUrl(url, new URLSearchParams(loginForm));
                await postFetcher(url).then((data) => {
                    if(data.success) {
                        onLoggedInData(data);
                    }
                    isLoading.value = false;
                }).catch((error) => {
                    console.log(error);
                });
            }
        }

        const onAddPerson = (personObj) => {
            let ticketConfig = ticketConfigurations.value;
            if(ticketConfig['persons'][personObj.id] === undefined){
                ticketConfig['persons'][personObj.id] = {};
            }
            ticketConfig['persons'][personObj.id].counter = 1;
            ticketConfig['persons'][personObj.id].priceGroup = personObj.priceGroup;
            ticketConfig['persons'][personObj.id].price = personObj.price;
            changeTicketConfigurations(ticketConfig);
            calculatePrice();

            // open the special price modal if it exists and the person is added
            const priceGroup = parseInt(personObj.priceGroup);
            const specialPriceModal = ticketStore.specialPriceModals.find(modal => modal.priceGroup === priceGroup);

            if (specialPriceModal && specialPriceModal.priceGroup === priceGroup) {
                modalTitle.value = specialPriceModal.title;
                modalContent.value = specialPriceModal.text;
                showOverlay.value = true;
            }
        }

        const onRemovePerson = (personId) => {
            let ticketConfig = ticketConfigurations.value;
            if(ticketConfig['persons'][personId] === undefined){
                ticketConfig['persons'][personId] = {};
            }
            ticketConfig['persons'][personId].counter = 0;
            changeTicketConfigurations(ticketConfig);
            calculatePrice();

            const person = ticketConfig['persons'][personId];
            if (person && modalShown.value[person.priceGroup]) {
                showOverlay.value = false;
                modalShown.value[person.priceGroup] = false;
            }
        }

        const calculatePrice = () => {
            let ticketConfig = ticketConfigurations.value;
            let total = 0;

            allPriceGroups.value.forEach(function(priceGroup) {
                if(ticketConfig['priceGroups'][priceGroup.value]?.counter) {
                    total += getPriceGroupSum(pricePerPriceGroup(priceGroup.value).price, ticketConfig['priceGroups'][priceGroup.value].counter);
                }
            })

            //map over ticketConfig['persons'] and add price to total
            Object.keys(ticketConfig['persons']).map((item, key) => {
                let person = ticketConfig['persons'][item];
                if (person.counter > 0) {
                    total += pricePerPriceGroup(person.priceGroup).price;
                }
            })

            ticketConfig['totalPrice'] = total;
            changeFinalPrice(total);
        }

        const pricePerPriceGroup = (priceGroup: String) => {
            return selectedPriceConfiguration.value?.priceByPriceGroup ? selectedPriceConfiguration.value.priceByPriceGroup.filter((element) => element.id === priceGroup)[0] : {};
        }

        const getPriceGroupSum = (price: Number, priceGroupCounter: Number) => {
            return price * priceGroupCounter;
        }

        const showOverlay = ref(false);
        const modalShown = ref<{ [key: string]: boolean }>({});
        const modalTitle = ref("");
        const modalContent = ref("");

        const closeOverlay = () => {
            showOverlay.value = false;
        };

        const checkSpecialPriceModal = (inputId, increment) => {
            if (ticketStore.hideSpecialPriceModal) {
                return;
            }

            if (specialPriceModals.value.length === 0) {
                console.warn("specialPriceModals is empty");
                return;
            }

            let amount = 0;
            const specialPriceGroup = specialPriceModals.value.find(modal => modal.priceGroup === inputId);

            if (specialPriceGroup) {
                if (increment) {
                    amount += 1;
                } else {
                    amount -= 1;
                }

                if (specialPriceGroup.title && specialPriceGroup.text) {
                    const category = specialPriceGroup.priceGroup.toString();

                    if (!modalShown.value[category]) {
                        modalShown.value[category] = false;
                    }

                    if (amount > 0 && !modalShown.value[category]) {
                        modalTitle.value = specialPriceGroup.title;
                        modalContent.value = specialPriceGroup.text;
                        showOverlay.value = true;
                        modalShown.value[category] = true;
                    }

                    if (amount <= 0) {
                        modalShown.value[category] = false;
                    }
                }
            }
        };

        return {
            step,
            selectedDateStart,
            selectedDateEnd,
            selectedOption,
            selectedInsurance,
            selectedUpgrades,
            changeStep,
            onChangeStep,
            priceGroups,
            priceConfigurations,
            selectedPriceGroup,
            selectedPriceConfiguration,
            allPriceGroups,
            onChangePriceGroupCounter,
            ticketConfigurations,
            selectedCurrency,
            translate,
            priceGroupCounter,
            isLoading,
            onLoggedInData,
            profilePersons,
            onAddPerson,
            isMobile,
            onRemovePerson,
            showOverlay,
            modalShown,
            modalTitle,
            modalContent,
            closeOverlay,
            specialPriceModals,
            checkSpecialPriceModal,
        };
    }
});
